import React, { useState, useEffect, useRef, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { Lock, LogOut, Trash2, Loader2, Mail, ArrowLeft, ShieldX } from "lucide-react";
import { useToast } from "../hooks/use-toast";
import apiService from "../services/api";

export default function Inbox() {
  const { toast } = useToast();
  const location = useLocation();
  const hasProcessed = useRef(false);
  // user: null = checking, false = not authenticated, object = authenticated owner
  const [user, setUser] = useState(null);
  const [denied, setDenied] = useState(false);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const init = async () => {
      const hash = location.hash || "";
      if (hash.includes("session_id=")) {
        if (hasProcessed.current) return;
        hasProcessed.current = true;
        const sessionId = new URLSearchParams(hash.substring(1)).get("session_id");
        try {
          const u = await apiService.exchangeSession(sessionId);
          window.history.replaceState(null, "", window.location.pathname);
          setUser(u);
        } catch (e) {
          window.history.replaceState(null, "", window.location.pathname);
          if (e?.response?.status === 403) setDenied(true);
          setUser(false);
        }
        return;
      }
      try {
        const u = await apiService.getMe();
        setUser(u);
      } catch (e) {
        if (e?.response?.status === 403) setDenied(true);
        setUser(false);
      }
    };
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchMessages = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiService.getInboxMessages();
      setMessages(data);
    } catch (e) {
      if (e?.response?.status === 401 || e?.response?.status === 403) {
        setUser(false);
        toast({ title: "Session expired", description: "Sign in again.", variant: "destructive" });
      }
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    if (user && user !== false) fetchMessages();
  }, [user, fetchMessages]);

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/inbox";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleLogout = async () => {
    try {
      await apiService.authLogout();
    } finally {
      setUser(false);
      setDenied(false);
      setMessages([]);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Permanently delete this message?")) return;
    try {
      await apiService.deleteInboxMessage(id);
      setMessages((prev) => prev.filter((m) => m.id !== id));
      toast({ title: "Message deleted" });
    } catch (e) {
      if (e?.response?.status === 401 || e?.response?.status === 403) {
        setUser(false);
        toast({ title: "Session expired", description: "Sign in again.", variant: "destructive" });
      } else {
        toast({ title: "Delete failed", variant: "destructive" });
      }
    }
  };

  if (user === null) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center" data-testid="inbox-checking">
        <div className="flex items-center gap-3 font-mono text-sm text-gray-500">
          <Loader2 className="h-4 w-4 animate-spin" /> VERIFYING CLEARANCE...
        </div>
      </div>
    );
  }

  if (user === false) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center px-6" data-testid="inbox-login-screen">
        <div className="w-full max-w-sm border border-white/10 bg-[#0a0a0a] p-10">
          {denied ? (
            <ShieldX className="h-6 w-6 text-red-400 mb-6" />
          ) : (
            <Lock className="h-6 w-6 text-emerald-400 mb-6" />
          )}
          <p className="font-mono text-xs tracking-[0.3em] text-emerald-400 mb-2">RESTRICTED AREA</p>
          <h1 className="font-display text-2xl text-gray-100 mb-4">Vault Access</h1>
          {denied && (
            <p className="text-sm text-red-400 mb-4 font-mono" data-testid="inbox-denied-message">
              Access denied — this Google account is not the owner.
            </p>
          )}
          <p className="text-sm text-gray-500 mb-8">Sign in with the owner's Google account to read visitor messages.</p>
          <button
            onClick={handleGoogleLogin}
            className="w-full bg-emerald-500 text-black hover:bg-emerald-400 transition-colors font-mono tracking-wider uppercase text-sm px-6 py-3 inline-flex items-center justify-center gap-2"
            data-testid="inbox-google-login-btn"
          >
            <svg className="h-4 w-4" viewBox="0 0 24 24" aria-hidden="true">
              <path fill="currentColor" d="M21.35 11.1h-9.17v2.73h6.51c-.33 3.81-3.5 5.44-6.5 5.44C8.36 19.27 5 16.25 5 12c0-4.1 3.2-7.27 7.2-7.27 3.09 0 4.9 1.97 4.9 1.97L19 4.72S16.56 2 12.1 2C6.42 2 2.03 6.8 2.03 12c0 5.05 4.13 10 10.22 10 5.35 0 9.25-3.67 9.25-9.09 0-1.15-.15-1.81-.15-1.81Z"/>
            </svg>
            Sign in with Google
          </button>
          <a href="/" className="font-mono text-xs text-gray-600 hover:text-emerald-400 transition-colors mt-8 inline-flex items-center gap-2" data-testid="inbox-back-link">
            <ArrowLeft className="h-3 w-3" /> BACK TO SITE
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] px-6 md:px-12 py-16" data-testid="inbox-screen">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center justify-between mb-12">
          <div>
            <p className="font-mono text-xs tracking-[0.3em] text-emerald-400 mb-2 flex items-center gap-2">
              <Mail className="h-4 w-4" /> SECURE INBOX
            </p>
            <h1 className="font-display text-3xl text-gray-100">Visitor Messages</h1>
          </div>
          <div className="flex items-center gap-4">
            {user.picture && (
              <img src={user.picture} alt={user.name} className="h-8 w-8 border border-emerald-500/40" data-testid="inbox-user-avatar" />
            )}
            <span className="font-mono text-xs text-gray-500 hidden sm:inline" data-testid="inbox-user-email">{user.email}</span>
            <a href="/" className="font-mono text-xs text-gray-500 hover:text-emerald-400 transition-colors" data-testid="inbox-home-link">SITE</a>
            <button
              onClick={handleLogout}
              className="font-mono text-xs tracking-wider uppercase text-gray-400 hover:text-red-400 border border-white/10 px-4 py-2 transition-colors inline-flex items-center gap-2"
              data-testid="inbox-logout-btn"
            >
              <LogOut className="h-3 w-3" /> Lock
            </button>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center gap-3 font-mono text-sm text-gray-500" data-testid="inbox-loading">
            <Loader2 className="h-4 w-4 animate-spin" /> Decrypting messages...
          </div>
        ) : messages.length === 0 ? (
          <p className="font-mono text-sm text-gray-500 border border-white/10 p-10 text-center" data-testid="inbox-empty">
            [ NO MESSAGES IN THE VAULT ]
          </p>
        ) : (
          <div className="space-y-px bg-white/10 border border-white/10" data-testid="inbox-message-list">
            {messages.map((m) => (
              <article key={m.id} className="bg-[#0a0a0a] p-6 hover:bg-[#0d0d0d] transition-colors" data-testid={`inbox-message-${m.id}`}>
                <div className="flex items-start justify-between gap-4 mb-3">
                  <div>
                    <p className="text-gray-100 text-sm font-medium">{m.subject}</p>
                    <p className="font-mono text-xs text-emerald-400 mt-1">
                      {m.name} · {m.email}
                    </p>
                  </div>
                  <div className="flex items-center gap-4 shrink-0">
                    <span className="font-mono text-[10px] text-gray-600">
                      {new Date(m.created_at).toLocaleString()}
                    </span>
                    <button
                      onClick={() => handleDelete(m.id)}
                      className="text-gray-600 hover:text-red-400 transition-colors"
                      aria-label="Delete message"
                      data-testid={`inbox-delete-btn-${m.id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
                <p className="text-sm text-gray-400 leading-relaxed whitespace-pre-wrap">{m.message}</p>
              </article>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
